import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageLayout from '../../components/layout/PageLayout';
import { useExportSaleInformationQuery } from '../../store/api';
import { toast } from 'react-toastify';

const SALE_COLUMNS = [
  { key: 'client_name', label: 'Client name' },
  { key: 'status', label: 'Status' },
  { key: 'total_amount', label: 'Total amount', money: true },
  { key: 'paid_amount', label: 'Paid amount', money: true },
  { key: 'refund', label: 'Refund', money: true },
  { key: 'pending_amount', label: 'Pending amount', money: true },
  { key: 'front_sale', label: 'Front sale', money: true },
  { key: 'up_sale', label: 'Up sale', money: true },
  // Referral sales are already counted in front/up above; this breaks out how
  // much of that came through referrals.
  { key: 'referral_sale', label: 'Referral', money: true },
  { key: 'down_sell', label: 'Down sell', money: true },
];

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const cellValue = (row, col) => {
  const v = row[col.key];
  if (v === undefined || v === null || v === '') return col.money ? formatMoney(0) : '—';
  if (col.money) return formatMoney(v);
  return String(v);
};

export default function GenerateSalesInformation() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading: loading, error } = useExportSaleInformationQuery();

  const rows = useMemo(() => {
    const list = data?.data ?? data?.list ?? (Array.isArray(data) ? data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || 'Failed to load sale information report');
    }
  }, [error]);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const searchLower = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!searchLower) return rows;
    return rows.filter((row) =>
      SALE_COLUMNS.some((c) => String(row[c.key] ?? '').toLowerCase().includes(searchLower))
    );
  }, [rows, searchLower]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
           Sales Information
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search rows…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    {SALE_COLUMNS.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.money ? 'right' : 'left'}
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={SALE_COLUMNS.length} align="center" sx={{ py: 6 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={SALE_COLUMNS.length} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No data returned for this report.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row, idx) => (
                      <TableRow key={row.id ?? `${row.client_name}-${idx}`} hover>
                        {SALE_COLUMNS.map((c) => (
                          <TableCell
                            key={c.key}
                            align={c.money ? 'right' : 'left'}
                            sx={{ whiteSpace: 'nowrap', maxWidth: 220 }}
                          >
                            <Typography variant="body2" noWrap title={String(row[c.key] ?? '')}>
                              {cellValue(row, c)}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && filtered.length > 0 && (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
}
